import React, { useState, useMemo, useEffect, useRef, useCallback, startTransition } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import NewDemandModal from '../components/common/NewDemandModal';
import { DemandDetailModal } from '../components/common/DemandDetailModal';
import DemandsHeatMap from '../components/common/DemandsHeatMap';
import { useAuth } from '../contexts/AuthContext';
import { PermissionGuard } from '../components/common/PermissionGuard';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import UserAvatar from '../components/common/UserAvatar';
import { API_BASE_URL } from '../lib/apiConfig';
import { apiFetch } from '../lib/apiFetch';

// Types conforme banco de dados
type DemandType = 'INDIVIDUAL' | 'CIDADE' | 'ENTIDADE' | 'GRUPO';
type DemandSize = 'PEQUENA' | 'MEDIA' | 'GRANDE';
type DemandNature = 'EXCLUSIVA' | 'INTERMEDIADA';
type DemandStatus = 'NAO_INICIADA' | 'EM_ANDAMENTO' | 'PAUSADA' | 'CANCELADA' | 'CONCLUIDA';

interface Cidade {
  id: number;
  nome: string;
  uf: string;
}

interface Solicitante {
  id: number;
  nome: string;
  telefone?: string;
}

interface Responsavel {
  id: number;
  nome: string;
  foto_url?: string | null;
}

interface Registro {
  id: number;
  descricao_atualizacao: string;
  data: string;
  status_na_data?: DemandStatus;
  created_at: string;
  updated_at: string;
  usuario: {
    id: number;
    nome: string;
  };
}

interface HistoricoItem {
  id: number;
  observacao: string;
  data_hora: string;
  usuario: {
    id: number;
    nome: string;
  } | null;
}

interface TeamMember {
  id: number;
  nome: string;
  tipo_usuario: 'ADMIN' | 'ASSESSOR';
  nivel_acesso: string;
  foto_url?: string | null;
}

interface Demand {
  id: number;
  titulo: string;
  descricao?: string;
  tipo: DemandType;
  tamanho?: DemandSize;
  natureza?: DemandNature;
  status: DemandStatus;
  data_inicio?: string;
  data_fim?: string;
  data_cadastro: string;
  cidade_id?: number;
  cidade?: Cidade;
  solicitante_id?: number;
  solicitante?: Solicitante;
  responsavel_id?: number;
  responsavel?: Responsavel;
}

interface DemandFormData {
  titulo: string;
  descricao: string;
  tipo: DemandType | '';
  tamanho: DemandSize | '';
  natureza: DemandNature | '';
  cidade_id: number | null;
  solicitante_id: number | null;
  intermediador: string;
  previsao_conclusao: string;
}

// Traduções dos status
const statusLabels: Record<DemandStatus, string> = {
  NAO_INICIADA: 'Não Iniciada',
  EM_ANDAMENTO: 'Em Andamento',
  PAUSADA: 'Pausada',
  CANCELADA: 'Cancelada',
  CONCLUIDA: 'Concluída'
};

// Traduções dos tipos
const typeLabels: Record<DemandType, string> = {
  INDIVIDUAL: 'Individual',
  CIDADE: 'Cidade',
  ENTIDADE: 'Entidade',
  GRUPO: 'Grupo'
};

// Styled Components
const DemandsContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f7f8fa;
  padding: 32px 40px;
  padding-bottom: 120px;
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px 16px 100px 16px;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AddButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    align-items: center;
    margin-bottom: 16px;
  }
`;

const PageHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ViewModeButtons = styled.div`
  display: flex;
  gap: 8px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
  margin-left: 16px;
`;

const ViewModeButton = styled.button<{ $active: boolean }>`
  padding: 8px 12px;
  background: ${props => props.$active ? '#ffffff' : 'transparent'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.$active ? 'var(--accent)' : '#64748b'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.$active ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: var(--accent);
  }
`;

const MapFullScreen = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 56px);
  overflow: hidden;
  background: #ffffff;
`;

const MapTopLeft = styled.div`
  position: absolute;
  top: 32px;
  left: 40px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MapTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MapTitleIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(var(--accent-rgb), 0.08);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MapPageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.1;
`;

const MapPageSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  margin: 4px 0 0 0;
`;

const MapTitleDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #e2e8f0;
  margin: 18px 0 12px;
`;

const MapSearchOverlay = styled.div`
  position: relative;
  width: 320px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const MapSearchInput = styled.input`
  width: 100%;
  padding: 16px 20px 16px 52px;
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 50px;
  font-size: 15px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(var(--accent-rgb), 0.3);
    box-shadow: 
      0 6px 30px rgba(0, 0, 0, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 1);
  }
  
  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 1);
    border-color: var(--accent);
    box-shadow: 
      0 8px 40px rgba(var(--accent-rgb), 0.15),
      0 4px 12px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 1),
      0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const MapAddButton = styled.button`
  position: absolute;
  top: 20px;
  right: 24px;
  z-index: 20;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;

  svg { width: 20px; height: 20px; }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.4);
  }
  &:active { transform: translateY(0); }
`;

const MapTopRight = styled.div`
  position: absolute;
  top: 32px;
  right: 40px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: 42px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #64748b;
  margin: 0;
`;

const AddButton = styled.button`
  background: var(--accent);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.15s;
  white-space: nowrap;
  letter-spacing: -0.1px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15), 0 4px 12px rgba(var(--accent-rgb), 0.3);

  svg {
    width: 17px;
    height: 17px;
    stroke-width: 2.5;
  }

  &:hover {
    background: var(--accent-dark);
    box-shadow: 0 2px 6px rgba(0,0,0,0.15), 0 6px 16px rgba(var(--accent-rgb), 0.35);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  }

  .add-btn-text { }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 13px;
    border-radius: 8px;
    svg { width: 15px; height: 15px; }
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 180px;
  max-width: 480px;
`;

const SearchIconWrap = styled.div`
  position: absolute;
  left: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  pointer-events: none;
  svg { width: 15px; height: 15px; }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 32px 9px 36px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #374151;
  font-size: 13.5px;
  font-weight: 500;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;

  &::placeholder { color: #94a3b8; font-weight: 400; }
  &:focus { outline: none; border-color: #93c5fd; box-shadow: 0 0 0 3px rgba(147,197,253,0.25); }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  transition: color 0.2s ease;
  border-radius: 50%;

  &:hover {
    color: #64748b;
    background: #f1f5f9;
  }

  svg { width: 15px; height: 15px; }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const FilterChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'white'};
  border: 1px solid ${p => p.$active ? 'rgba(var(--accent-rgb), 0.4)' : '#e2e8f0'};
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: ${p => p.$active ? '600' : '500'};
  color: ${p => p.$active ? 'var(--accent)' : '#64748b'};
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  touch-action: manipulation;

  svg { width: 13px; height: 13px; }

  &:hover {
    border-color: rgba(var(--accent-rgb), 0.4);
    color: var(--accent);
    background: rgba(var(--accent-rgb), 0.06);
  }
`;

const ChipX = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(var(--accent-rgb), 0.2);
  color: var(--accent);
  font-size: 13px;
  line-height: 1;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: rgba(var(--accent-rgb), 0.35); }
`;

const FilterWrapper = styled.div`
  position: relative;

  @media (max-width: 768px) {
    flex-shrink: 0;
  }
`;

const SortSelect = styled.select`
  padding: 8px 14px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 28px;

  &:focus {
    outline: none;
    border-color: var(--accent);
    color: var(--accent);
    background-color: rgba(var(--accent-rgb), 0.06);
  }
`;

const FilterDropdown = styled.div<{ $desktopAlign?: 'left' | 'right'; $mobileTop?: number; $mobileLeft?: number; $mobileWidth?: number }>`
  position: absolute;
  top: calc(100% + 6px);
  ${p => p.$desktopAlign === 'left' ? 'left: 0;' : 'right: 0;'}
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  min-width: 200px;
  max-width: 280px;
  z-index: 100;
  overflow: hidden;

  @media (max-width: 768px) {
    position: fixed;
    top: ${p => `${p.$mobileTop ?? 0}px`};
    left: ${p => `${p.$mobileLeft ?? 16}px`};
    right: auto;
    width: ${p => `${p.$mobileWidth ?? 220}px`};
    min-width: 0;
    max-width: calc(100vw - 32px);
  }
`;

const FilterOption = styled.div<{ $active: boolean }>`
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.$active ? 'var(--accent)' : '#1e293b'};
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'transparent'};
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.12)' : '#f8fafc'};
  }

  @media (hover: none), (pointer: coarse) {
    &:hover {
      background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'transparent'};
    }
  }
`;

const DropdownSearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #1e293b;
  box-sizing: border-box;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-bottom-color: var(--accent);
  }
`;

const MapStatsOverlay = styled.div<{ $isHidden?: boolean }>`
  position: absolute;
  bottom: 30px;
  left: 16px;
  z-index: 20;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 320px;
  opacity: ${p => p.$isHidden ? '0' : '1'};
  transform: translateX(${p => p.$isHidden ? '-10px' : '0'});
  pointer-events: ${p => p.$isHidden ? 'none' : 'auto'};
  transition: all 0.2s ease;
`;

const MapStatItem = styled.div<{ $active: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 10px 12px;
  border-radius: 8px;
  transition: all 0.15s;
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'transparent'};
  &:hover { background: #f8fafc; }
`;

const MapStatIcon = styled.div<{ $color: string }>`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 8px;
  background: ${p => p.$color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.$color};
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const MapStatContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 8px;
  flex: 1;
`;

const MapStatValue = styled.div<{ $active?: boolean }>`
  font-size: 24px;
  font-weight: 800;
  color: ${p => p.$active ? 'var(--accent)' : '#1e293b'};
  line-height: 1;
`;

const MapStatLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  line-height: 1;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-bottom: 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    background: transparent;
    border: none;
    border-radius: 0;
    gap: 10px;
    overflow: visible;
  }

  @media (max-width: 640px) {
    display: flex;
    overflow-x: auto;
    background: transparent;
    border: none;
    border-radius: 0;
    gap: 10px;
    padding: 2px 0 10px;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

const StatCard = styled.div<{ $active?: boolean; $accentColor?: string }>`
  padding: 16px 20px;
  border-right: 1px solid #f1f5f9;
  border-bottom: 3px solid ${p => p.$active && p.$accentColor ? p.$accentColor : 'transparent'};
  background: ${p => p.$active ? '#f8fafc' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s;
  position: relative;

  &:last-child { border-right: none; }
  &:hover { background: #f8fafc; }

  @media (max-width: 900px) {
    border-right: none;
    border-radius: 14px;
    border: 1.5px solid ${p => p.$active && p.$accentColor ? p.$accentColor + '55' : '#e8edf2'};
    background: ${p => p.$active && p.$accentColor
      ? `linear-gradient(to bottom, ${p.$accentColor}14 0%, #ffffff 100%)`
      : 'linear-gradient(to bottom, #eef0f5 0%, #ffffff 100%)'};
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }

  @media (max-width: 640px) {
    flex-shrink: 0;
    min-width: 120px;
    padding: 10px 14px;
    border-radius: 12px;
  }
`;

const StatCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const StatDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
`;

const StatCardLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;

  @media (max-width: 640px) {
    font-size: 10px;
    letter-spacing: 0.4px;
  }
`;

const StatCardValue = styled.div`
  font-size: 30px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  margin-bottom: 2px;

  @media (max-width: 640px) {
    font-size: 22px;
  }
`;

const StatCardSub = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 500;

  @media (max-width: 640px) {
    font-size: 10px;
  }
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: 12px;
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 28px;
    height: 28px;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 8px;
    svg { width: 20px; height: 20px; }
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 2px;
  }
`;

const StatLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 11px;
    letter-spacing: 0;
  }
`;

const DemandsTable = styled.div`
  background: white;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  overflow: visible;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px 170px 140px 90px;
  padding: 10px 20px 10px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 14px 14px 0 0;
`;

const TableHeaderCell = styled.div<{ $align?: 'left' | 'right'; $sortable?: boolean }>`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  text-align: ${props => props.$align === 'right' ? 'right' : 'left'};
  cursor: ${props => props.$sortable ? 'pointer' : 'default'};
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;

  ${props => props.$sortable && `&:hover { color: #64748b; }`}
`;

const TableRow = styled.div<{ $statusColor?: string }>`
  display: grid;
  grid-template-columns: 1fr 120px 170px 140px 90px;
  padding: 0 20px 0 0;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.12s;
  cursor: pointer;
  border-left: 3px solid ${p => p.$statusColor || 'transparent'};
  padding-left: 21px;

  &:hover { background: #f8fafc; }
  &:last-child { border-bottom: none; border-radius: 0 0 14px 14px; }
`;

const TableCell = styled.div<{ $align?: 'left' | 'right'; $allowOverflow?: boolean }>`
  font-size: 13.5px;
  color: #374151;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$align === 'right' ? 'flex-end' : 'flex-start'};
  overflow: ${props => props.$allowOverflow ? 'visible' : 'hidden'};
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0;
  position: relative;
`;

const TitleCellContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 13px 0;
  min-width: 0;
`;

const TitleMain = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TitleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: nowrap;
`;

const TitleTag = styled.span`
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
`;

const TitleMetaText = styled.span`
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
`;

const TitleMetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #cbd5e1;
  flex-shrink: 0;
`;

const StatusInlineDot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
`;

const CityTableCell = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CadastradoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CadastradoDate = styled.div`
  font-size: 13px;
  color: #374151;
  font-weight: 500;
`;

const CadastradoRelative = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
`;

const SlaTableCell = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${p => p.$color};
`;

const SlaDash = styled.div<{ $color: string }>`
  width: 3px;
  height: 18px;
  border-radius: 2px;
  background: ${p => p.$color};
  flex-shrink: 0;
`;

const SlaValue = styled.span`
  font-size: 13px;
  font-weight: 600;
`;

const DemandId = styled.span`
  color: var(--accent);
  font-weight: 700;
`;

const StatusBadge = styled.div<{ $status: DemandStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  ${props => {
    switch (props.$status) {
      case 'NAO_INICIADA': return `background: #fee2e2; color: #991b1b;`;
      case 'EM_ANDAMENTO': return `background: #fef3c7; color: #92400e;`;
      case 'PAUSADA': return `background: #f1f5f9; color: #64748b;`;
      case 'CONCLUIDA': return `background: #d1fae5; color: #065f46;`;
      case 'CANCELADA': return `background: #fee2e2; color: #991b1b;`;
      default: return `background: #f1f5f9; color: #64748b;`;
    }
  }}
`;

const SLABadge = styled.div<{ $days: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 700;
  ${props => {
    if (props.$days <= 7) return `background: #d1fae5; color: #065f46;`;
    if (props.$days <= 30) return `background: #fef3c7; color: #92400e;`;
    return `background: #fee2e2; color: #991b1b;`;
  }}
`;

const EmptyState = styled.div`
  padding: 80px 20px;
  text-align: center;
`;

const EmptyStateText = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
`;

const EmptyStateSubtext = styled.div`
  font-size: 15px;
  color: #64748b;
`;

const LoadingState = styled.div`
  padding: 80px 20px;
  text-align: center;
  font-size: 18px;
  color: #64748b;
`;

const ShimmerLine = styled.div<{ width?: string; height?: string }>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '14px'};
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    #e2e8f0 0%,
    #f1f5f9 50%,
    #e2e8f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const ShimmerCircle = styled.div`
  border-radius: 50%;
  background: linear-gradient(
    90deg,
    #e2e8f0 0%,
    #f1f5f9 50%,
    #e2e8f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const ShimmerBadge = styled.div<{ width?: string }>`
  width: ${props => props.width || '100px'};
  height: 28px;
  border-radius: 50px;
  background: linear-gradient(
    90deg,
    #e2e8f0 0%,
    #f1f5f9 50%,
    #e2e8f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const StickyHeader = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 16px 40px;
  display: ${props => props.$visible ? 'flex' : 'none'};
  justify-content: space-between;
  align-items: center;
  z-index: 1999;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const StickyHeaderTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const StickyHeaderButton = styled.button`
  padding: 12px 24px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.3);

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: var(--accent-dark);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(var(--accent-rgb), 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ResponsibleCell = styled.div<{ $isAdmin?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background 0.1s;
  cursor: ${props => props.$isAdmin ? 'pointer' : 'default'};
  overflow: hidden;
  white-space: nowrap;
  width: 100%;

  ${props => props.$isAdmin && `
    &:hover {
      background: #f1f5f9;
    }
  `}
`;

const ResponsibleAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #e2e8f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ResponsibleName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
`;

const ResponsibleDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
  z-index: 1000;
  min-width: 260px;
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const ResponsibleOption = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.08s;

  &:hover {
    background: #f8fafc;
  }

  &:first-child {
    border-radius: 11px 11px 0 0;
  }

  &:last-child {
    border-radius: 0 0 11px 11px;
  }
`;

const OptionAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OptionName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
`;

const OptionRole = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.3;
`;

const StatusCell = styled.div<{ $canEdit?: boolean }>`
  position: relative;
  cursor: ${props => props.$canEdit ? 'pointer' : 'default'};
  width: fit-content;
  
  ${props => props.$canEdit && `
    &:hover {
      opacity: 0.8;
    }
  `}
`;

const StatusDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 180px;
  overflow: hidden;
`;

const StatusOption = styled.div<{ $status: DemandStatus }>`
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #f8fafc;
  }

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    ${props => {
      switch (props.$status) {
        case 'NAO_INICIADA':
          return 'background: #92400e;';
        case 'EM_ANDAMENTO':
          return 'background: #1e40af;';
        case 'PAUSADA':
          return 'background: #92400e;';
        case 'CONCLUIDA':
          return 'background: #065f46;';
        case 'CANCELADA':
          return 'background: #991b1b;';
        default:
          return 'background: #64748b;';
      }
    }}
  }
`;

const StatusOptionText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
`;

const UnassignedText = styled.span`
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
`;

const LoadingSpinner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--accent);
  font-size: 13px;
  font-weight: 500;

  &::after {
    content: '';
    width: 16px;
    height: 16px;
    border: 2px solid #e2e8f0;
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SortIcon = styled.span<{ $active: boolean; $direction: 'asc' | 'desc' }>`
  display: inline-flex;
  font-size: 10px;
  opacity: ${props => props.$active ? 1 : 0.3};
  
  &::before {
    content: '${props => props.$direction === 'asc' ? '▲' : '▼'}';
  }
`;

// Bottom Sheet Components
const BottomSheetOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const BottomSheet = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(${props => props.$isOpen ? '0' : '100%'});
  max-width: 800px;
  width: 100%;
  max-height: 85vh;
  background: white;
  border-radius: 24px 24px 0 0;
  box-shadow: 0 -4px 40px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    max-width: 100%;
    left: 0;
    right: 0;
    transform: translateY(${props => props.$isOpen ? '0' : '100%'});
  }
`;

const BottomSheetHandle = styled.div`
  width: 40px;
  height: 5px;
  background: #cbd5e1;
  border-radius: 3px;
  margin: 12px auto 8px;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
`;

const BottomSheetHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BottomSheetTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CloseBottomSheetButton = styled.button`
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
  }

  svg {
    width: 16px;
    height: 16px;
    stroke: #475569;
  }
`;

const BottomSheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const DetailSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const DetailValue = styled.div`
  font-size: 15px;
  color: #1e293b;
  font-weight: 500;
  line-height: 1.5;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

// --- Mobile demand list ---
const MobileSearchInput = styled.input`
  display: none;

  @media (max-width: 768px) {
    display: block;
    width: 100%;
    padding: 8px 14px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #475569;
    font-size: 13px;
    font-weight: 600;
    box-sizing: border-box;
    margin-bottom: 8px;
    transition: all 0.15s;

    &::placeholder { color: #94a3b8; font-weight: 500; }
    &:focus { outline: none; border-color: var(--accent); color: var(--accent); background: rgba(var(--accent-rgb), 0.06); }
  }
`;

const MobileDemandList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;

const MobileDemandCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:active {
    background: #f8fafc;
    border-color: rgba(var(--accent-rgb), 0.3);
  }
`;

const MobileDemandShimmer = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
`;

const MobileDemandCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
`;

const MobileDemandTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  flex: 1;
  line-height: 1.3;
`;

const MobileDemandMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;

const MobileDemandMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;

  &:not(:last-child)::after {
    content: '·';
    margin-left: 8px;
    color: #cbd5e1;
  }
`;

const MobileDemandResponsible = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MobileDemandAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MobileDemandResponsibleName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`;

const MobileDemandNoResponsible = styled.span`
  font-size: 13px;
  color: #cbd5e1;
  font-style: italic;
`;

// ─── Mobile redesign ─────────────────────────────────────────────────────────

const DemandsDesktopHeaderBtns = styled.div`
  @media (max-width: 768px) { display: none; }
`;

const DemandsMobileTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DemandsMobileSubtitle = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    font-size: 13px;
    color: #94a3b8;
    font-weight: 500;
    margin-top: 2px;
  }
`;

const DemandsMobileNewBtn = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 12px;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    &:active { opacity: 0.85; }
  }
`;

const DemandsMobileSearchRow = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }
`;

const DemandsMobileSearchWrap = styled.div`
  position: relative;
  flex: 1;
`;

const DemandsMobileSearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  display: flex;
  pointer-events: none;
`;

const DemandsMobileSearchInput = styled.input`
  width: 100%;
  padding: 10px 36px 10px 36px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  box-sizing: border-box;
  &::placeholder { color: #94a3b8; }
  &:focus { outline: none; border-color: #93c5fd; box-shadow: 0 0 0 3px rgba(147,197,253,0.25); }
`;

const DemandsMobileFilterBtn = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background: white;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  flex-shrink: 0;
  cursor: pointer;
  &:active { background: #f1f5f9; }
`;

const DemandsMobileChipsRow = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 4px;
    margin-bottom: 12px;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

const DemandsMobileChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'white'};
  border: 1px solid ${p => p.$active ? 'rgba(var(--accent-rgb), 0.4)' : '#e2e8f0'};
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: ${p => p.$active ? '600' : '500'};
  color: ${p => p.$active ? 'var(--accent)' : '#64748b'};
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  svg { width: 12px; height: 12px; }
`;

const DemandsMobileChipX = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: rgba(var(--accent-rgb), 0.2);
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
`;

const DemandsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 12px 0 8px;
`;

const DemandsSectionDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
`;

const DemandsSectionLabel = styled.span<{ $color: string }>`
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.$color};
  text-transform: uppercase;
  letter-spacing: 0.7px;
`;

const DemandsSectionCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
`;

const MobileNewDemandCard = styled.div<{ $borderColor: string }>`
  background: white;
  border: 1px solid #e8edf2;
  border-left: 3.5px solid ${p => p.$borderColor};
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  &:active { box-shadow: 0 2px 8px rgba(59,130,246,0.1); background: #fafbfc; }
`;

const MobileNewDemandCardInner = styled.div`
  padding: 13px 14px 10px;
`;

const MobileNewDemandTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
`;

const MobileNewDemandTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  flex: 1;
  line-height: 1.35;
`;

const MobileNewDemandMenuBtn = styled.button`
  background: none;
  border: none;
  padding: 0 2px;
  color: #94a3b8;
  cursor: pointer;
  font-size: 18px;
  letter-spacing: 1.5px;
  flex-shrink: 0;
  line-height: 1;
`;

const MobileNewDemandMetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 6px;
  margin-bottom: 10px;
`;

const MobileNewDemandTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const MobileNewDemandMetaDot = styled.span`
  color: #cbd5e1;
  font-size: 11px;
`;

const MobileNewDemandMetaText = styled.span`
  font-size: 12.5px;
  color: #64748b;
  font-weight: 500;
`;

const MobileNewDemandResponsibleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const MobileNewDemandResponsibleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  flex: 1;
`;

const MobileNewDemandResponsibleName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MobileNewDemandSlaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
`;

const MobileNewDemandSlaDash = styled.div<{ $color: string }>`
  width: 20px;
  height: 3px;
  border-radius: 2px;
  background: ${p => p.$color};
`;

const MobileNewDemandSlaText = styled.span<{ $color: string }>`
  font-size: 12.5px;
  font-weight: 700;
  color: ${p => p.$color};
  white-space: nowrap;
`;

const MobileNewDemandFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 14px;
  border-top: 1px dashed #e2e8f0;
`;

const statusBadgeBg: Record<DemandStatus, string> = { NAO_INICIADA: '#fee2e2', EM_ANDAMENTO: '#dbeafe', PAUSADA: '#f1f5f9', CONCLUIDA: '#dcfce7', CANCELADA: '#f1f5f9' };
const statusBadgeColor: Record<DemandStatus, string> = { NAO_INICIADA: '#dc2626', EM_ANDAMENTO: '#1d4ed8', PAUSADA: '#64748b', CONCLUIDA: '#059669', CANCELADA: '#64748b' };

const MobileNewDemandStatusBadge = styled.span<{ $status: DemandStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => statusBadgeBg[p.$status]};
  color: ${p => statusBadgeColor[p.$status]};
`;

const MobileNewDemandDateText = styled.span`
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 500;
`;

const DemandFilterOverlay = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 9997;
    opacity: ${p => p.$open ? 1 : 0};
    visibility: ${p => p.$open ? 'visible' : 'hidden'};
    transition: opacity 0.25s, visibility 0.25s;
  }
`;

const DemandFilterSheet = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 82vh;
    background: white;
    border-radius: 20px 20px 0 0;
    z-index: 9998;
    transform: translateY(${p => p.$open ? '0' : '100%'});
    transition: transform 0.3s ease;
    overflow: hidden;
  }
`;

const DemandFilterHandle = styled.div`
  width: 36px;
  height: 4px;
  background: #cbd5e1;
  border-radius: 2px;
  margin: 10px auto 6px;
  flex-shrink: 0;
`;

const DemandFilterSheetTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  padding: 8px 20px 14px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

const DemandFilterSheetBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
`;

const DemandFilterSection = styled.div`
  margin-bottom: 20px;
`;

const DemandFilterSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 10px;
`;

const DemandFilterOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DemandFilterOption = styled.button<{ $active: boolean }>`
  padding: 7px 14px;
  border-radius: 999px;
  border: 1.5px solid ${p => p.$active ? 'var(--accent)' : '#e2e8f0'};
  background: ${p => p.$active ? '#eff6ff' : 'white'};
  color: ${p => p.$active ? 'var(--accent)' : '#475569'};
  font-size: 13px;
  font-weight: ${p => p.$active ? '600' : '500'};
  cursor: pointer;
`;

const DemandFilterApplyBtn = styled.button`
  margin: 0 20px 20px;
  padding: 13px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
`;

// ─── DemandModalController ───────────────────────────────────────────────────
// Isolated in its own memo component so opening the modal never re-renders the
// demand list — state changes here are invisible to the parent render tree.

interface DemandModalControllerProps {
  controllerRef: React.MutableRefObject<{ open: (d: any) => void } | null>;
  onDemandUpdate: () => void;
  canViewUpdates: boolean;
  canCreateUpdates: boolean;
  canEditUpdate: (r: any) => boolean;
  canDeleteUpdate: (r: any) => boolean;
  canEditDemand: (d: any) => boolean;
  canDeleteDemand: (d: any) => boolean;
  canAssignResponsible: boolean;
  canUnassignResponsible: boolean;
  userData: any;
  session: any;
}

const DemandModalController = React.memo(function DemandModalController({
  controllerRef,
  onDemandUpdate,
  canViewUpdates,
  canCreateUpdates,
  canEditUpdate,
  canDeleteUpdate,
  canEditDemand,
  canDeleteDemand,
  canAssignResponsible,
  canUnassignResponsible,
  userData,
  session,
}: DemandModalControllerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [demand, setDemand] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'registros'>('info');
  const [registros, setRegistros] = useState<any[]>([]);
  const [loadingRegistros, setLoadingRegistros] = useState(false);
  const [historico, setHistorico] = useState<any[]>([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [newRegistro, setNewRegistro] = useState('');
  const [editingRegistro, setEditingRegistro] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [addingRegistro, setAddingRegistro] = useState(false);
  const [savingRegistroId, setSavingRegistroId] = useState<number | null>(null);
  const [deletingRegistroId, setDeletingRegistroId] = useState<number | null>(null);

  const fetchRegistros = useCallback(async (demandaId: number) => {
    if (!session?.access_token) return;
    setLoadingRegistros(true);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${demandaId}/registros`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (r.ok) { const d = await r.json(); setRegistros(d.data || []); }
    } finally { setLoadingRegistros(false); }
  }, [session?.access_token]);

  const fetchHistorico = useCallback(async (demandaId: number) => {
    if (!session?.access_token) return;
    setLoadingHistorico(true);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${demandaId}/historico`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (r.ok) { const d = await r.json(); setHistorico(d.data || []); }
    } finally { setLoadingHistorico(false); }
  }, [session?.access_token]);

  // Expose open() to parent via ref — calling this never re-renders the list
  useEffect(() => {
    controllerRef.current = {
      open: (d: any) => {
        setDemand(d);
        setIsOpen(true);
        setRegistros([]);
        setHistorico([]);
        if (canViewUpdates) {
          fetchRegistros(d.id);
          fetchHistorico(d.id);
        }
      },
    };
  }, [controllerRef, canViewUpdates, fetchRegistros, fetchHistorico]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setDemand(null);
    setActiveTab('info');
    setRegistros([]);
    setHistorico([]);
    setNewRegistro('');
    setEditingRegistro(null);
    setEditingText('');
  }, []);

  const handleAddRegistro = useCallback(async () => {
    if (!demand?.id || !newRegistro.trim() || !session?.access_token) return;
    setAddingRegistro(true);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${demand.id}/registros`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ descricao_atualizacao: newRegistro.trim() }),
      });
      if (r.ok) { setNewRegistro(''); fetchRegistros(demand.id); }
      else alert('Erro ao adicionar registro');
    } catch { alert('Erro ao adicionar registro'); }
    finally { setAddingRegistro(false); }
  }, [demand, newRegistro, session, fetchRegistros]);

  const handleEditRegistro = useCallback(async (registroId: number) => {
    if (!demand?.id || !editingText.trim() || !session?.access_token) return;
    setSavingRegistroId(registroId);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${demand.id}/registros/${registroId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ descricao_atualizacao: editingText.trim() }),
      });
      if (r.ok) { setEditingRegistro(null); setEditingText(''); fetchRegistros(demand.id); }
      else alert('Erro ao editar registro');
    } catch { alert('Erro ao editar registro'); }
    finally { setSavingRegistroId(null); }
  }, [demand, editingText, session, fetchRegistros]);

  const handleDeleteRegistro = useCallback(async (registroId: number) => {
    if (!demand?.id || !session?.access_token) return;
    setDeletingRegistroId(registroId);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${demand.id}/registros/${registroId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (r.ok) fetchRegistros(demand.id);
      else alert('Erro ao excluir registro');
    } catch { alert('Erro ao excluir registro'); }
    finally { setDeletingRegistroId(null); }
  }, [demand, session, fetchRegistros]);

  return (
    <DemandDetailModal
      isOpen={isOpen}
      demand={demand}
      onClose={handleClose}
      onUpdate={() => { onDemandUpdate(); if (demand?.id && canViewUpdates) fetchHistorico(demand.id); }}
      activeTab={activeTab}
      onTabChange={(tab) => {
        if (!canViewUpdates && tab === 'registros') return;
        setActiveTab(tab);
        if (tab === 'registros' && demand?.id && registros.length === 0) {
          fetchRegistros(demand.id);
          fetchHistorico(demand.id);
        }
      }}
      registros={registros}
      loadingRegistros={loadingRegistros}
      newRegistro={newRegistro}
      onNewRegistroChange={setNewRegistro}
      onAddRegistro={canCreateUpdates ? handleAddRegistro : undefined}
      addingRegistro={addingRegistro}
      editingRegistro={editingRegistro}
      editingText={editingText}
      onEditRegistro={(id) => {
        const reg = registros.find(r => r.id === id);
        if (reg && canEditUpdate(reg)) { setEditingRegistro(id); setEditingText(reg.descricao_atualizacao); }
      }}
      onSaveEdit={handleEditRegistro}
      onCancelEdit={() => { setEditingRegistro(null); setEditingText(''); }}
      onEditTextChange={setEditingText}
      onDeleteRegistro={(id) => {
        const reg = registros.find(r => r.id === id);
        if (reg && canDeleteUpdate(reg)) handleDeleteRegistro(id);
      }}
      savingRegistroId={savingRegistroId}
      deletingRegistroId={deletingRegistroId}
      openMenuId={openMenuId}
      onMenuToggle={setOpenMenuId}
      canViewUpdates={canViewUpdates}
      canCreateUpdates={canCreateUpdates}
      canEditUpdate={canEditUpdate}
      canDeleteUpdate={canDeleteUpdate}
      canEditDemand={demand ? canEditDemand(demand) : false}
      canDeleteDemand={demand ? canDeleteDemand(demand) : false}
      canAssignResponsible={canAssignResponsible}
      canUnassignResponsible={canUnassignResponsible}
      userData={userData}
      historico={historico}
      loadingHistorico={loadingHistorico}
    />
  );
});

// ─────────────────────────────────────────────────────────────────────────────

interface DemandsProps {
  showHeaderButton?: boolean;
  triggerAdd?: number;
  onHeaderButtonClick?: () => void;
  displayMode?: 'map' | 'list';
  onDisplayModeChange?: (mode: 'map' | 'list') => void;
}

const Demands: React.FC<DemandsProps> = ({ showHeaderButton = false, triggerAdd = 0, onHeaderButtonClick, displayMode = 'list', onDisplayModeChange }) => {
  const { session, isAdmin, userData } = useAuth();
  const { can, canAny, loading: permissionsLoading } = usePermissionCheck();
  const location = useLocation();
  const [demands, setDemands] = useState<Demand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [responsibleFilter, setResponsibleFilter] = useState<string>('all');
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showResponsibleDropdown, setShowResponsibleDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const responsibleButtonRef = useRef<HTMLButtonElement | null>(null);
  const cityButtonRef = useRef<HTMLButtonElement | null>(null);
  const sortButtonRef = useRef<HTMLButtonElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 16, width: 220 });
  const [responsibleSearchTerm, setResponsibleSearchTerm] = useState('');
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [filterTotal, setFilterTotal] = useState(false);
  const [filterPending, setFilterPending] = useState(false);
  const [filterInProgress, setFilterInProgress] = useState(false);
  const [filterCompleted, setFilterCompleted] = useState(false);
  const [filterUnassigned, setFilterUnassigned] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  const [showMobileFilterSheet, setShowMobileFilterSheet] = useState(false);
  const modalControllerRef = useRef<{ open: (d: any) => void } | null>(null);
  // Inline list-row dropdowns (independent of modal state)
  const [openResponsibleDropdown, setOpenResponsibleDropdown] = useState<number | null>(null);
  const [assigningResponsible, setAssigningResponsible] = useState<number | null>(null);
  const [openStatusDropdown, setOpenStatusDropdown] = useState<number | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [visibleMax, setVisibleMax] = useState(1);
  const [visibleCount, setVisibleCount] = useState(40);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const mobileSentinelRef = useRef<HTMLDivElement>(null);

  // Permission checks
  const canViewDemands = canAny(['demands.view.all', 'demands.view.own', 'demands.view.city']);
  const canCreateDemand = can('demands.create') || can('demands.create.scoped_city');
  const canEditAll = can('demands.edit.all');
  const canEditOwn = can('demands.edit.own');
  const canDeleteAll = can('demands.delete.all');
  const canDeleteOwn = can('demands.delete.own');
  const canDeleteUnassigned = can('demands.delete.unassigned');
  const canChangeStatusAll = can('demands.status.change.all');
  const canChangeStatusOwn = can('demands.status.change.own');
  const canAssignResponsible = can('demands.assign.responsible');
  const canUnassignResponsible = can('demands.unassign');
  const canViewUpdates = can('demands.updates.view');
  const canCreateUpdates = can('demands.updates.create');
  const canEditAllUpdates = can('demands.updates.edit.all');
  const canEditOwnUpdates = can('demands.updates.edit.own');
  const canDeleteAllUpdates = can('demands.updates.delete.all');
  const canDeleteOwnUpdates = can('demands.updates.delete.own');
  
  // Helper functions to check permissions for specific demands
  const canEditDemand = (demand: Demand) => {
    return canEditAll || (canEditOwn && demand.responsavel_id === userData?.id);
  };

  const canDeleteDemand = (demand: Demand) => {
    if (canDeleteAll) return true;
    if (canDeleteOwn && demand.responsavel_id === userData?.id) return true;
    if (canDeleteUnassigned && !demand.responsavel_id) return true;
    return false;
  };

  const canChangeStatus = (demand: Demand) => {
    return canChangeStatusAll || (canChangeStatusOwn && demand.responsavel_id === userData?.id);
  };

  const canEditUpdate = (registro: Registro) => {
    return canEditAllUpdates || (canEditOwnUpdates && registro.usuario?.id === userData?.id);
  };

  const canDeleteUpdate = (registro: Registro) => {
    return canDeleteAllUpdates || (canDeleteOwnUpdates && registro.usuario?.id === userData?.id);
  };

  // Helper functions - declared before hooks that use them
  const fetchDemands = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/demandas`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDemands(data.data || []);
      } else {
        console.error('Erro ao carregar demandas:', response.statusText);
      }
    } catch (error) {
      console.error('Erro ao carregar demandas:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTeamMembers = async () => {
    if (!session?.access_token) return;

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/usuarios/grupo`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setTeamMembers(result.data);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar membros do time:', error);
    }
  };

  const calculateSLA = (dataCadastro: string, status: DemandStatus): number => {
    // Se já foi concluída ou cancelada, não calcula SLA
    if (status === 'CONCLUIDA' || status === 'CANCELADA') {
      return 0;
    }

    const cadastro = new Date(dataCadastro);
    const hoje = new Date();
    const diffTime = Math.abs(hoje.getTime() - cadastro.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const updateDropdownPosition = (button: HTMLButtonElement | null, align: 'left' | 'right' = 'right') => {
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const width = Math.min(Math.max(Math.round(rect.width), 220), window.innerWidth - 32);
    const maxLeft = Math.max(16, window.innerWidth - width - 16);
    const desiredLeft = align === 'left' ? rect.left : rect.right - width;
    const left = Math.min(Math.max(16, Math.round(desiredLeft)), maxLeft);

    setDropdownPosition({
      top: Math.round(rect.bottom + 6),
      left,
      width,
    });
  };

  // Carregar demandas ao montar o componente
  useEffect(() => {
    if (session?.access_token) {
      fetchDemands();
      fetchTeamMembers();
    }
  }, [session]);

  // Watch for trigger from header button
  React.useEffect(() => {
    if (triggerAdd > 0) {
      setIsSideSheetOpen(true);
    }
  }, [triggerAdd]);

  // Handle navigation from contacts page with openDemandId
  useEffect(() => {
    const state = location.state as { openDemandId?: number } | null;
    if (state?.openDemandId && demands.length > 0) {
      const demand = demands.find(d => d.id === state.openDemandId);
      if (demand) {
        modalControllerRef.current?.open(demand);
        // Clear the state to prevent reopening on page refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, demands]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[role="dialog"]') || target.closest('.modal-container')) return;
      if (!target.closest('[data-filter-dropdown]')) {
        setShowStatusDropdown(false);
        setShowResponsibleDropdown(false);
        setShowCityDropdown(false);
        setShowSortDropdown(false);
      }
      setOpenResponsibleDropdown(null);
      setOpenStatusDropdown(null);
    };
    if (openResponsibleDropdown !== null || openStatusDropdown !== null || showStatusDropdown || showResponsibleDropdown || showCityDropdown || showSortDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openResponsibleDropdown, openStatusDropdown, showStatusDropdown, showResponsibleDropdown, showCityDropdown, showSortDropdown]);

  // Filter demands
  const filteredDemands = useMemo(() => {
    let filtered = demands.filter(demand => {
      const matchesSearch = !searchTerm ||
        demand.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demand.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demand.cidade?.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demand.solicitante?.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demand.responsavel?.nome.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || demand.status === statusFilter;
      
      const matchesResponsible = responsibleFilter === 'all' || 
        (responsibleFilter === 'unassigned' && !demand.responsavel) ||
        (demand.responsavel && demand.responsavel.id.toString() === responsibleFilter);
      
      const matchesCity = cityFilter === 'all' || 
        (demand.cidade && demand.cidade.id.toString() === cityFilter);

      // Big number filters
      const matchesBigNumberFilters = 
        (!filterTotal && !filterPending && !filterInProgress && !filterCompleted && !filterUnassigned) || // nenhum filtro ativo
        filterTotal || // se Total está ativo, mostra tudo
        (filterPending && demand.status === 'NAO_INICIADA') ||
        (filterInProgress && demand.status === 'EM_ANDAMENTO') ||
        (filterCompleted && demand.status === 'CONCLUIDA') ||
        (filterUnassigned && !demand.responsavel);

      return matchesSearch && matchesStatus && matchesResponsible && matchesCity && matchesBigNumberFilters;
    });

    // Aplicar ordenação
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortColumn) {
          case 'id':
            aValue = a.id;
            bValue = b.id;
            break;
          case 'data':
            aValue = new Date(a.data_cadastro).getTime();
            bValue = new Date(b.data_cadastro).getTime();
            break;
          case 'titulo':
            aValue = a.titulo.toLowerCase();
            bValue = b.titulo.toLowerCase();
            break;
          case 'tipo':
            aValue = a.tipo;
            bValue = b.tipo;
            break;
          case 'cidade':
            aValue = a.cidade?.nome.toLowerCase() || '';
            bValue = b.cidade?.nome.toLowerCase() || '';
            break;
          case 'responsavel':
            aValue = a.responsavel?.nome.toLowerCase() || 'zzz';
            bValue = b.responsavel?.nome.toLowerCase() || 'zzz';
            break;
          case 'sla':
            aValue = calculateSLA(a.data_cadastro, a.status);
            bValue = calculateSLA(b.data_cadastro, b.status);
            break;
          case 'status':
            aValue = a.status;
            bValue = b.status;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [demands, searchTerm, statusFilter, responsibleFilter, cityFilter, sortColumn, sortDirection, filterTotal, filterPending, filterInProgress, filterCompleted, filterUnassigned]);

  // Mobile grouped demands by SLA criticality / status
  const groupedDemands = useMemo(() => {
    const getSlaGroup = (demand: Demand) => {
      const sla = calculateSLA(demand.data_cadastro, demand.status);
      if (demand.status === 'CONCLUIDA' || demand.status === 'CANCELADA') return 5;
      if (sla > 30) return 1; // SLA CRÍTICO
      if (sla > 10) return 2; // EM ATENÇÃO
      if (demand.status === 'EM_ANDAMENTO') return 3;
      if (demand.status === 'NAO_INICIADA') return 4;
      return 5;
    };
    const groups: { key: string; label: string; color: string; dot: string; items: Demand[] }[] = [
      { key: 'critico',     label: 'SLA Crítico',   color: '#dc2626', dot: '#ef4444', items: [] },
      { key: 'atencao',     label: 'Em Atenção',    color: '#b45309', dot: '#f59e0b', items: [] },
      { key: 'andamento',   label: 'Em Andamento',  color: '#1d4ed8', dot: '#3b82f6', items: [] },
      { key: 'nao_iniciada',label: 'Não Iniciada',  color: '#64748b', dot: '#94a3b8', items: [] },
      { key: 'concluida',   label: 'Concluída',     color: '#059669', dot: '#10b981', items: [] },
    ];
    filteredDemands.slice(0, visibleCount).forEach(d => {
      const g = getSlaGroup(d);
      groups[g - 1].items.push(d);
    });
    return groups.filter(g => g.items.length > 0);
  }, [filteredDemands, visibleCount]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(40);
  }, [filteredDemands.length]);

  // IntersectionObserver for infinite scroll sentinel
  useEffect(() => {
    const trigger = () => {
      if (!loadingMore && visibleCount < filteredDemands.length) {
        setLoadingMore(true);
        setTimeout(() => {
          setVisibleCount(prev => prev + 40);
          setLoadingMore(false);
        }, 150);
      }
    };
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) trigger(); },
      { threshold: 0.1 }
    );
    if (sentinelRef.current) obs.observe(sentinelRef.current);
    if (mobileSentinelRef.current) obs.observe(mobileSentinelRef.current);
    return () => obs.disconnect();
  }, [loadingMore, visibleCount, filteredDemands.length]);

  // Calculate stats based on filtered demands (excluding big number filters)
  const stats = useMemo(() => {
    const baseFiltered = demands.filter(demand => {
      const matchesSearch = 
        demand.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demand.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demand.cidade?.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demand.solicitante?.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demand.responsavel?.nome.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || demand.status === statusFilter;
      const matchesResponsible = responsibleFilter === 'all' || 
        (responsibleFilter === 'unassigned' && !demand.responsavel) ||
        (demand.responsavel && demand.responsavel.id.toString() === responsibleFilter);
      const matchesCity = cityFilter === 'all' || 
        (demand.cidade && demand.cidade.id.toString() === cityFilter);

      return matchesSearch && matchesStatus && matchesResponsible && matchesCity;
    });

    const pendingList = baseFiltered.filter(d => d.status === 'NAO_INICIADA');
    const inProgressList = baseFiltered.filter(d => d.status === 'EM_ANDAMENTO');
    const now = Date.now();
    const dayMs = 1000 * 60 * 60 * 24;
    const criticalSla = pendingList.filter(d => Math.ceil(Math.abs(now - new Date(d.data_cadastro).getTime()) / dayMs) > 30).length;
    const avgInProgressSla = inProgressList.length > 0
      ? Math.round(inProgressList.reduce((s, d) => s + Math.ceil(Math.abs(now - new Date(d.data_cadastro).getTime()) / dayMs), 0) / inProgressList.length)
      : 0;
    return {
      total: baseFiltered.length,
      pending: pendingList.length,
      inProgress: inProgressList.length,
      completed: baseFiltered.filter(d => d.status === 'CONCLUIDA').length,
      unassigned: baseFiltered.filter(d => !d.responsavel).length,
      criticalSla,
      avgInProgressSla,
    };
  }, [demands, searchTerm, statusFilter, responsibleFilter, cityFilter]);

  // Calculate demand counts by city for heat map (using filteredDemands)
  const demandCounts = useMemo(() => {
    const counts: Record<number, { 
      total: number; 
      naoIniciada: number; 
      emAndamento: number; 
      concluida: number; 
      naoAtribuida: number;
    }> = {};
    
    filteredDemands.forEach(demand => {
      if (demand.cidade_id) {
        if (!counts[demand.cidade_id]) {
          counts[demand.cidade_id] = {
            total: 0,
            naoIniciada: 0,
            emAndamento: 0,
            concluida: 0,
            naoAtribuida: 0
          };
        }
        counts[demand.cidade_id].total++;
        
        if (!demand.responsavel) {
          counts[demand.cidade_id].naoAtribuida++;
        }
        
        if (demand.status === 'NAO_INICIADA') {
          counts[demand.cidade_id].naoIniciada++;
        } else if (demand.status === 'EM_ANDAMENTO') {
          counts[demand.cidade_id].emAndamento++;
        } else if (demand.status === 'CONCLUIDA') {
          counts[demand.cidade_id].concluida++;
        }
      }
    });
    
    return counts;
  }, [filteredDemands]);

  const handleDemandClick = useCallback((demand: Demand) => {
    modalControllerRef.current?.open(demand);
  }, []);

  const handleRowClick = useCallback((demand: Demand) => {
    modalControllerRef.current?.open(demand);
  }, []);

  const handleDemandUpdate = useCallback(() => {
    fetchDemands();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show shimmer while permissions are still loading
  if (permissionsLoading) {
    return (
      <DemandsContainer>
        <PageHeader>
          <PageHeaderText>
            <ShimmerLine width="200px" height="32px" style={{ borderRadius: 8 }} />
            <ShimmerLine width="300px" height="16px" style={{ borderRadius: 4, marginTop: 6 }} />
          </PageHeaderText>
          <ShimmerLine width="140px" height="42px" style={{ borderRadius: 50 }} />
        </PageHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0', borderBottom: '1px solid #f1f5f9' }}>
              <ShimmerLine width="120px" height="14px" />
              <ShimmerLine width="200px" height="14px" />
              <ShimmerBadge width="90px" />
              <ShimmerLine width="100px" height="14px" />
              <ShimmerLine width="80px" height="14px" />
            </div>
          ))}
        </div>
      </DemandsContainer>
    );
  }

  // Check if user has any view permission - do this check after all hooks
  if (!canViewDemands) {
    return (
      <DemandsContainer>
        <AccessDeniedContainer>
          <AccessDeniedIcon>
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="64" height="64">
              <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </AccessDeniedIcon>
          <AccessDeniedTitle>Acesso Negado</AccessDeniedTitle>
          <AccessDeniedText>
            Você não tem permissão para visualizar demandas.
          </AccessDeniedText>
        </AccessDeniedContainer>
      </DemandsContainer>
    );
  }

  const handleAddClick = () => {
    setIsSideSheetOpen(true);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatRelativeDate = (dateString: string): string => {
    const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'hoje';
    if (diff === 1) return 'há 1 dia';
    return `há ${diff} dias`;
  };

  const statusAccentColor: Record<DemandStatus, string> = {
    NAO_INICIADA: '#ef4444',
    EM_ANDAMENTO: '#3b82f6',
    PAUSADA: '#94a3b8',
    CONCLUIDA: '#10b981',
    CANCELADA: '#6b7280',
  };

  const getSlaColor = (days: number): string => {
    if (days > 30) return '#ef4444';
    if (days > 10) return '#b45309';
    return '#10b981';
  };

  const handleDemandSubmit = async (formData: DemandFormData) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/demandas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        // Recarregar lista de demandas
        await fetchDemands();
        setIsSideSheetOpen(false);
      } else {
        const error = await response.json();
        alert(`Erro ao criar demanda: ${error.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao criar demanda:', error);
      alert('Erro ao criar demanda. Tente novamente.');
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Se já está ordenando por esta coluna, inverte a direção
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nova coluna, começa com ascendente
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const toggleBigNumberFilter = (filter: 'total' | 'pending' | 'inProgress' | 'completed' | 'unassigned') => {
    switch (filter) {
      case 'total':
        setFilterTotal(!filterTotal);
        setFilterPending(false);
        setFilterInProgress(false);
        setFilterCompleted(false);
        setFilterUnassigned(false);
        setResponsibleFilter('all');
        break;
      case 'pending':
        setFilterPending(!filterPending);
        setFilterTotal(false);
        setFilterInProgress(false);
        setFilterCompleted(false);
        setFilterUnassigned(false);
        setResponsibleFilter('all');
        break;
      case 'inProgress':
        setFilterInProgress(!filterInProgress);
        setFilterTotal(false);
        setFilterPending(false);
        setFilterCompleted(false);
        setFilterUnassigned(false);
        setResponsibleFilter('all');
        break;
      case 'completed':
        setFilterCompleted(!filterCompleted);
        setFilterTotal(false);
        setFilterPending(false);
        setFilterInProgress(false);
        setFilterUnassigned(false);
        setResponsibleFilter('all');
        break;
      case 'unassigned':
        setFilterUnassigned(!filterUnassigned);
        setFilterTotal(false);
        setFilterPending(false);
        setFilterInProgress(false);
        setFilterCompleted(false);
        break;
    }
  };

  const handleResponsibleClick = (e: React.MouseEvent, demandId: number) => {
    if (!canAssignResponsible && !canUnassignResponsible) return;
    e.stopPropagation();
    setOpenResponsibleDropdown(openResponsibleDropdown === demandId ? null : demandId);
  };

  const handleAssignResponsible = async (demandId: number, userId: number) => {
    if (!session?.access_token) return;

    setAssigningResponsible(demandId);
    setOpenResponsibleDropdown(null);

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${demandId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ responsavel_id: userId }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Atualizar apenas a demanda específica no estado local
        setDemands(prevDemands => 
          prevDemands.map(demand => 
            demand.id === demandId 
              ? { ...demand, responsavel: result.data.responsavel }
              : demand
          )
        );
      } else {
        alert('Erro ao atribuir responsável');
      }
    } catch (error) {
      console.error('Erro ao atribuir responsável:', error);
      alert('Erro ao atribuir responsável');
    } finally {
      setAssigningResponsible(null);
    }
  };

  const getPhotoUrl = (id: number) => {
    const avatarId = (id % 70) + 1;
    return `https://i.pravatar.cc/150?img=${avatarId}`;
  };

  const canEditStatus = (demand: Demand) => {
    return canChangeStatus(demand);
  };

  const handleStatusClick = (e: React.MouseEvent, demandId: number, demand: Demand) => {
    if (!canEditStatus(demand)) return;
    e.stopPropagation();
    setOpenStatusDropdown(openStatusDropdown === demandId ? null : demandId);
  };

  const handleStatusChange = async (demandId: number, newStatus: DemandStatus) => {
    if (!session?.access_token) return;

    setUpdatingStatus(demandId);
    setOpenStatusDropdown(null);

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${demandId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Atualizar apenas a demanda específica no estado local
        setDemands(prevDemands => 
          prevDemands.map(demand => 
            demand.id === demandId 
              ? { ...demand, status: result.data.status }
              : demand
          )
        );
      } else {
        alert('Erro ao atualizar status');
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  return (
    <>
      {canCreateDemand && (
        <StickyHeader $visible={showHeaderButton}>
          <StickyHeaderTitle>Demandas</StickyHeaderTitle>
          <StickyHeaderButton onClick={onHeaderButtonClick}>
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 4v16m8-8H4" />
            </svg>
            Adicionar Demanda
          </StickyHeaderButton>
        </StickyHeader>
      )}

      {displayMode === 'map' ? (
        <MapFullScreen>
          <MapTopLeft>
            <MapTitleRow>
              <MapTitleIcon>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </MapTitleIcon>
              <MapPageTitle>Demandas</MapPageTitle>
            </MapTitleRow>
            <MapPageSubtitle>Gerencie todas as demandas da campanha</MapPageSubtitle>
            <MapTitleDivider />
            <MapSearchOverlay>
              <svg
                style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#94a3b8', pointerEvents: 'none', zIndex: 1 }}
                fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <MapSearchInput
                type="text"
                placeholder="Buscar demanda..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
              />
            </MapSearchOverlay>
          </MapTopLeft>

          <MapTopRight>
            <PermissionGuard permissions={['demands.create', 'demands.create.scoped_city']}>
              <MapAddButton onClick={handleAddClick}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Nova Demanda
              </MapAddButton>
            </PermissionGuard>
          </MapTopRight>

          <DemandsHeatMap
            demandCounts={demandCounts}
            loading={loading}
            onVisibleMaxChange={(max) => setVisibleMax(max)}
          />

          {/* Big numbers — canto esquerdo (vertical) */}
          <MapStatsOverlay $isHidden={searchTerm.trim().length >= 2}>
            <MapStatItem
              $active={filterTotal}
              onClick={() => toggleBigNumberFilter('total')}
            >
              <MapStatIcon $color="#64748b">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </MapStatIcon>
              <MapStatContent>
                {loading ? (
                  <ShimmerLine width="60px" height="24px" />
                ) : (
                  <MapStatValue $active={filterTotal}>{stats.total}</MapStatValue>
                )}
                <MapStatLabel>Total</MapStatLabel>
              </MapStatContent>
            </MapStatItem>

            <MapStatItem
              $active={filterPending}
              onClick={() => toggleBigNumberFilter('pending')}
            >
              <MapStatIcon $color="#ef4444">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </MapStatIcon>
              <MapStatContent>
                {loading ? (
                  <ShimmerLine width="50px" height="24px" />
                ) : (
                  <MapStatValue $active={filterPending}>{stats.pending}</MapStatValue>
                )}
                <MapStatLabel>Não Iniciadas</MapStatLabel>
              </MapStatContent>
            </MapStatItem>

            <MapStatItem
              $active={filterInProgress}
              onClick={() => toggleBigNumberFilter('inProgress')}
            >
              <MapStatIcon $color="#f59e0b">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </MapStatIcon>
              <MapStatContent>
                {loading ? (
                  <ShimmerLine width="50px" height="24px" />
                ) : (
                  <MapStatValue $active={filterInProgress}>{stats.inProgress}</MapStatValue>
                )}
                <MapStatLabel>Em Andamento</MapStatLabel>
              </MapStatContent>
            </MapStatItem>

            <MapStatItem
              $active={filterCompleted}
              onClick={() => toggleBigNumberFilter('completed')}
            >
              <MapStatIcon $color="#10b981">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </MapStatIcon>
              <MapStatContent>
                {loading ? (
                  <ShimmerLine width="50px" height="24px" />
                ) : (
                  <MapStatValue $active={filterCompleted}>{stats.completed}</MapStatValue>
                )}
                <MapStatLabel>Concluídas</MapStatLabel>
              </MapStatContent>
            </MapStatItem>

            <MapStatItem
              $active={filterUnassigned}
              onClick={() => toggleBigNumberFilter('unassigned')}
            >
              <MapStatIcon $color="var(--accent)">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </MapStatIcon>
              <MapStatContent>
                {loading ? (
                  <ShimmerLine width="50px" height="24px" />
                ) : (
                  <MapStatValue $active={filterUnassigned}>{stats.unassigned}</MapStatValue>
                )}
                <MapStatLabel>Não Atribuídas</MapStatLabel>
              </MapStatContent>
            </MapStatItem>
          </MapStatsOverlay>
        </MapFullScreen>
      ) : (
        <DemandsContainer>
          <PageHeader>
            <PageHeaderText>
              <DemandsMobileTitleRow>
                <PageTitle>Demandas</PageTitle>
                <PermissionGuard permissions={['demands.create', 'demands.create.scoped_city']}>
                  <DemandsMobileNewBtn onClick={handleAddClick}>
                    <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width="14" height="14">
                      <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Nova
                  </DemandsMobileNewBtn>
                </PermissionGuard>
              </DemandsMobileTitleRow>
              <DemandsMobileSubtitle>{filteredDemands.length} demanda{filteredDemands.length !== 1 ? 's' : ''} ativa{filteredDemands.length !== 1 ? 's' : ''}</DemandsMobileSubtitle>
              <PageSubtitle>Gerencie todas as demandas da campanha</PageSubtitle>
            </PageHeaderText>
            <DemandsDesktopHeaderBtns>
              <PermissionGuard permissions={['demands.create', 'demands.create.scoped_city']}>
                <AddButton onClick={handleAddClick}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="add-btn-text">Nova demanda</span>
                </AddButton>
              </PermissionGuard>
            </DemandsDesktopHeaderBtns>
          </PageHeader>

          <Stats>
            <StatCard $active={filterTotal} $accentColor="#64748b" onClick={() => toggleBigNumberFilter('total')}>
              <StatCardTop>
                <StatCardLabel>TODAS</StatCardLabel>
              </StatCardTop>
              <StatCardValue>{loading ? '–' : stats.total}</StatCardValue>
              <StatCardSub>{stats.total === 1 ? '1 demanda ativa' : `${stats.total} demandas ativas`}</StatCardSub>
            </StatCard>
            <StatCard $active={filterPending} $accentColor="#ef4444" onClick={() => toggleBigNumberFilter('pending')}>
              <StatCardTop>
                <StatDot $color="#ef4444" />
                <StatCardLabel>NÃO INICIADAS</StatCardLabel>
              </StatCardTop>
              <StatCardValue>{loading ? '–' : stats.pending}</StatCardValue>
              <StatCardSub>{stats.criticalSla > 0 ? `${stats.criticalSla} com SLA crítico` : 'sem SLA crítico'}</StatCardSub>
            </StatCard>
            <StatCard $active={filterInProgress} $accentColor="#3b82f6" onClick={() => toggleBigNumberFilter('inProgress')}>
              <StatCardTop>
                <StatDot $color="#3b82f6" />
                <StatCardLabel>EM ANDAMENTO</StatCardLabel>
              </StatCardTop>
              <StatCardValue>{loading ? '–' : stats.inProgress}</StatCardValue>
              <StatCardSub>{stats.avgInProgressSla > 0 ? `tempo médio ${stats.avgInProgressSla} dias` : 'nenhuma em andamento'}</StatCardSub>
            </StatCard>
            <StatCard $active={filterCompleted} $accentColor="#10b981" onClick={() => toggleBigNumberFilter('completed')}>
              <StatCardTop>
                <StatDot $color="#10b981" />
                <StatCardLabel>CONCLUÍDAS</StatCardLabel>
              </StatCardTop>
              <StatCardValue>{loading ? '–' : stats.completed}</StatCardValue>
              <StatCardSub>demandas finalizadas</StatCardSub>
            </StatCard>
          </Stats>

          {/* Mobile search + filter chips */}
          <DemandsMobileSearchRow>
            <DemandsMobileSearchWrap>
              <DemandsMobileSearchIcon>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="15" height="15"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/></svg>
              </DemandsMobileSearchIcon>
              <DemandsMobileSearchInput
                type="text"
                placeholder="Buscar demandas..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <ClearSearchButton type="button" onClick={() => setSearchTerm('')} style={{ right: 10 }}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </ClearSearchButton>
              )}
            </DemandsMobileSearchWrap>
            <DemandsMobileFilterBtn onClick={() => setShowMobileFilterSheet(true)}>
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="17" height="17"><path d="M4 6h16M7 12h10M10 18h4" strokeLinecap="round"/></svg>
            </DemandsMobileFilterBtn>
          </DemandsMobileSearchRow>

          <DemandsMobileChipsRow>
            <DemandsMobileChip $active={responsibleFilter !== 'all'} onClick={() => {
              if (responsibleFilter !== 'all') { setResponsibleFilter('all'); } else { setShowMobileFilterSheet(true); }
            }}>
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
              {responsibleFilter === 'all' ? 'Responsável' : (responsibleFilter === 'unassigned' ? 'Não Atribuído' : teamMembers.find(m => m.id.toString() === responsibleFilter)?.nome || 'Responsável')}
              {responsibleFilter !== 'all' && <DemandsMobileChipX onClick={e => { e.stopPropagation(); setResponsibleFilter('all'); }}>×</DemandsMobileChipX>}
            </DemandsMobileChip>
            <DemandsMobileChip $active={cityFilter !== 'all'} onClick={() => {
              if (cityFilter !== 'all') { setCityFilter('all'); } else { setShowMobileFilterSheet(true); }
            }}>
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              {cityFilter === 'all' ? 'Cidade' : demands.find(d => d.cidade?.id.toString() === cityFilter)?.cidade?.nome || 'Cidade'}
              {cityFilter !== 'all' && <DemandsMobileChipX onClick={e => { e.stopPropagation(); setCityFilter('all'); }}>×</DemandsMobileChipX>}
            </DemandsMobileChip>
            <DemandsMobileChip $active={statusFilter !== 'all'} onClick={() => {
              if (statusFilter !== 'all') { setStatusFilter('all'); } else { setShowMobileFilterSheet(true); }
            }}>
              {statusFilter === 'all' ? 'Tipo' : statusLabels[statusFilter as DemandStatus]}
              {statusFilter !== 'all' && <DemandsMobileChipX onClick={e => { e.stopPropagation(); setStatusFilter('all'); }}>×</DemandsMobileChipX>}
            </DemandsMobileChip>
            <DemandsMobileChip $active={!!sortColumn} onClick={() => {
              if (sortColumn) { setSortColumn(''); } else { setShowMobileFilterSheet(true); }
            }}>
              <svg fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path d="M4 7h10M4 12h7M4 17h4M17 7v10m0 0-3-3m3 3 3-3" strokeLinecap="round" strokeLinejoin="round"/></svg>
              {!sortColumn ? 'Ordenar' : sortColumn === 'sla' && sortDirection === 'desc' ? 'Maior SLA' : sortColumn === 'sla' ? 'Menor SLA' : sortColumn === 'data' && sortDirection === 'desc' ? 'Mais recentes' : sortColumn === 'data' ? 'Mais antigas' : 'Título A–Z'}
              {sortColumn && <DemandsMobileChipX onClick={e => { e.stopPropagation(); setSortColumn(''); }}>×</DemandsMobileChipX>}
            </DemandsMobileChip>
          </DemandsMobileChipsRow>

          <Controls>
          <SearchWrapper>
            <SearchIconWrap>
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </SearchIconWrap>
            <SearchInput
              type="text"
              placeholder="Buscar por título, descrição, cidade, solicitante..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearSearchButton type="button" onClick={() => setSearchTerm('')}>
                <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </ClearSearchButton>
            )}
          </SearchWrapper>

          <FilterGroup>
            <FilterWrapper data-filter-dropdown>
              <FilterChip
                type="button"
                ref={cityButtonRef}
                $active={cityFilter !== 'all'}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowResponsibleDropdown(false);
                  setShowSortDropdown(false);
                  setShowStatusDropdown(false);
                  const next = !showCityDropdown;
                  if (next) updateDropdownPosition(cityButtonRef.current, 'left');
                  setShowCityDropdown(next);
                }}
              >
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                {cityFilter === 'all' ? 'Cidade' : demands.find(d => d.cidade?.id.toString() === cityFilter)?.cidade?.nome || 'Cidade'}
                {cityFilter !== 'all' ? (
                  <ChipX onClick={(e) => { e.stopPropagation(); setCityFilter('all'); }}>×</ChipX>
                ) : (
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                )}
              </FilterChip>
              {showCityDropdown && (
                <FilterDropdown $desktopAlign="left" $mobileTop={dropdownPosition.top} $mobileLeft={dropdownPosition.left} $mobileWidth={dropdownPosition.width} onClick={(e) => e.stopPropagation()}>
                  <DropdownSearchInput type="text" placeholder="Buscar cidade..." value={citySearchTerm} autoFocus onChange={(e) => setCitySearchTerm(e.target.value)} onClick={(e) => e.stopPropagation()} />
                  <FilterOption $active={cityFilter === 'all'} onClick={() => { setCityFilter('all'); setShowCityDropdown(false); setCitySearchTerm(''); }}>Todas as Cidades</FilterOption>
                  {Array.from(new Set(demands.filter(d => d.cidade).map(d => d.cidade!.id))).map(cityId => {
                    const city = demands.find(d => d.cidade?.id === cityId)?.cidade;
                    return city && city.nome.toLowerCase().includes(citySearchTerm.toLowerCase()) ? (
                      <FilterOption key={city.id} $active={cityFilter === city.id.toString()} onClick={() => { setCityFilter(city.id.toString()); setShowCityDropdown(false); setCitySearchTerm(''); }}>{city.nome}</FilterOption>
                    ) : null;
                  })}
                </FilterDropdown>
              )}
            </FilterWrapper>

            <FilterWrapper data-filter-dropdown>
              <FilterChip
                type="button"
                ref={responsibleButtonRef}
                $active={responsibleFilter !== 'all'}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCityDropdown(false);
                  setShowSortDropdown(false);
                  setShowStatusDropdown(false);
                  const next = !showResponsibleDropdown;
                  if (next) updateDropdownPosition(responsibleButtonRef.current, 'left');
                  setShowResponsibleDropdown(next);
                }}
              >
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                {responsibleFilter === 'all' ? 'Responsável' : responsibleFilter === 'unassigned' ? 'Não Atribuído' : teamMembers.find(m => m.id.toString() === responsibleFilter)?.nome || 'Responsável'}
                {responsibleFilter !== 'all' ? (
                  <ChipX onClick={(e) => { e.stopPropagation(); setResponsibleFilter('all'); }}>×</ChipX>
                ) : (
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                )}
              </FilterChip>
              {showResponsibleDropdown && (
                <FilterDropdown $desktopAlign="left" $mobileTop={dropdownPosition.top} $mobileLeft={dropdownPosition.left} $mobileWidth={dropdownPosition.width} onClick={(e) => e.stopPropagation()}>
                  <DropdownSearchInput type="text" placeholder="Buscar responsável..." autoFocus value={responsibleSearchTerm} onChange={(e) => setResponsibleSearchTerm(e.target.value)} onClick={(e) => e.stopPropagation()} />
                  <FilterOption $active={responsibleFilter === 'all'} onClick={() => { setResponsibleFilter('all'); setShowResponsibleDropdown(false); setResponsibleSearchTerm(''); }}>Todos os Responsáveis</FilterOption>
                  <FilterOption $active={responsibleFilter === 'unassigned'} onClick={() => { setResponsibleFilter('unassigned'); setShowResponsibleDropdown(false); setResponsibleSearchTerm(''); }}>Não Atribuído</FilterOption>
                  {teamMembers.filter(m => m.nome.toLowerCase().includes(responsibleSearchTerm.toLowerCase())).map(member => (
                    <FilterOption key={member.id} $active={responsibleFilter === member.id.toString()} onClick={() => { setResponsibleFilter(member.id.toString()); setShowResponsibleDropdown(false); setResponsibleSearchTerm(''); }}>{member.nome}</FilterOption>
                  ))}
                </FilterDropdown>
              )}
            </FilterWrapper>

            <FilterWrapper data-filter-dropdown>
              <FilterChip
                type="button"
                $active={statusFilter !== 'all'}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCityDropdown(false);
                  setShowResponsibleDropdown(false);
                  setShowSortDropdown(false);
                  setShowStatusDropdown(!showStatusDropdown);
                }}
              >
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/></svg>
                {statusFilter === 'all' ? 'Status' : statusLabels[statusFilter as DemandStatus]}
                {statusFilter !== 'all' ? (
                  <ChipX onClick={(e) => { e.stopPropagation(); setStatusFilter('all'); }}>×</ChipX>
                ) : (
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                )}
              </FilterChip>
              {showStatusDropdown && (
                <FilterDropdown $desktopAlign="left" onClick={(e) => e.stopPropagation()}>
                  {(['all', 'NAO_INICIADA', 'EM_ANDAMENTO', 'PAUSADA', 'CONCLUIDA', 'CANCELADA'] as const).map(s => (
                    <FilterOption key={s} $active={statusFilter === s} onClick={() => { setStatusFilter(s); setShowStatusDropdown(false); }}>
                      {s === 'all' ? 'Todos os status' : statusLabels[s]}
                    </FilterOption>
                  ))}
                </FilterDropdown>
              )}
            </FilterWrapper>

            <FilterWrapper data-filter-dropdown>
              <FilterChip
                type="button"
                ref={sortButtonRef}
                $active={!!sortColumn}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCityDropdown(false);
                  setShowResponsibleDropdown(false);
                  setShowStatusDropdown(false);
                  const next = !showSortDropdown;
                  if (next) updateDropdownPosition(sortButtonRef.current, 'right');
                  setShowSortDropdown(next);
                }}
              >
                <svg fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path d="M4 7h10"/><path d="M4 12h7"/><path d="M4 17h4"/><path d="M17 7v10"/><path d="m14 14 3 3 3-3"/></svg>
                {!sortColumn ? 'Ordenar' : sortColumn === 'sla' && sortDirection === 'desc' ? 'Maior SLA' : sortColumn === 'sla' && sortDirection === 'asc' ? 'Menor SLA' : sortColumn === 'data' && sortDirection === 'desc' ? 'Mais recentes' : sortColumn === 'data' && sortDirection === 'asc' ? 'Mais antigas' : 'Título A–Z'}
                {sortColumn ? (
                  <ChipX onClick={(e) => { e.stopPropagation(); setSortColumn(''); }}>×</ChipX>
                ) : (
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                )}
              </FilterChip>
              {showSortDropdown && (
                <FilterDropdown $desktopAlign="right" $mobileTop={dropdownPosition.top} $mobileLeft={dropdownPosition.left} $mobileWidth={dropdownPosition.width} onClick={(e) => e.stopPropagation()}>
                  <FilterOption $active={!sortColumn} onClick={() => { setSortColumn(''); setShowSortDropdown(false); }}>Sem ordenação</FilterOption>
                  <FilterOption $active={sortColumn === 'sla' && sortDirection === 'desc'} onClick={() => { setSortColumn('sla'); setSortDirection('desc'); setShowSortDropdown(false); }}>Maior SLA</FilterOption>
                  <FilterOption $active={sortColumn === 'sla' && sortDirection === 'asc'} onClick={() => { setSortColumn('sla'); setSortDirection('asc'); setShowSortDropdown(false); }}>Menor SLA</FilterOption>
                  <FilterOption $active={sortColumn === 'data' && sortDirection === 'desc'} onClick={() => { setSortColumn('data'); setSortDirection('desc'); setShowSortDropdown(false); }}>Mais recentes</FilterOption>
                  <FilterOption $active={sortColumn === 'data' && sortDirection === 'asc'} onClick={() => { setSortColumn('data'); setSortDirection('asc'); setShowSortDropdown(false); }}>Mais antigas</FilterOption>
                  <FilterOption $active={sortColumn === 'titulo'} onClick={() => { setSortColumn('titulo'); setSortDirection('asc'); setShowSortDropdown(false); }}>Título A–Z</FilterOption>
                </FilterDropdown>
              )}
            </FilterWrapper>
          </FilterGroup>
        </Controls>

        <DemandsTable>
          <TableHeader>
            <TableHeaderCell $sortable onClick={() => handleSort('titulo')}>
              TÍTULO <SortIcon $active={sortColumn === 'titulo'} $direction={sortDirection} />
            </TableHeaderCell>
            <TableHeaderCell $sortable onClick={() => handleSort('cidade')}>
              CIDADE <SortIcon $active={sortColumn === 'cidade'} $direction={sortDirection} />
            </TableHeaderCell>
            <TableHeaderCell $sortable onClick={() => handleSort('responsavel')}>
              RESPONSÁVEL <SortIcon $active={sortColumn === 'responsavel'} $direction={sortDirection} />
            </TableHeaderCell>
            <TableHeaderCell $sortable onClick={() => handleSort('data')}>
              CADASTRADO <SortIcon $active={sortColumn === 'data'} $direction={sortDirection} />
            </TableHeaderCell>
            <TableHeaderCell $sortable onClick={() => handleSort('sla')}>
              SLA <SortIcon $active={sortColumn === 'sla'} $direction={sortDirection} />
            </TableHeaderCell>
          </TableHeader>

          {loading ? (
            [1,2,3,4,5,6].map(i => (
              <TableRow key={i} $statusColor="#e2e8f0">
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '14px 0' }}>
                    <ShimmerLine width="60%" height="14px" />
                    <ShimmerLine width="40%" height="11px" />
                  </div>
                </TableCell>
                <TableCell><ShimmerLine width="80px" height="13px" /></TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ShimmerCircle style={{ width: 28, height: 28, minWidth: 28 }} />
                    <ShimmerLine width="90px" height="13px" />
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <ShimmerLine width="80px" height="13px" />
                    <ShimmerLine width="55px" height="11px" />
                  </div>
                </TableCell>
                <TableCell><ShimmerLine width="50px" height="13px" /></TableCell>
              </TableRow>
            ))
          ) : filteredDemands.length === 0 ? (
            <EmptyState>
              <EmptyStateText>Nenhuma demanda encontrada</EmptyStateText>
              <EmptyStateSubtext>
                {demands.length === 0 ? 'Comece criando sua primeira demanda' : 'Tente ajustar os filtros de busca'}
              </EmptyStateSubtext>
            </EmptyState>
          ) : (
            filteredDemands.slice(0, visibleCount).map((demand) => {
              const sla = calculateSLA(demand.data_cadastro, demand.status);
              const accentColor = statusAccentColor[demand.status];
              const slaColor = getSlaColor(sla);
              return (
                <TableRow key={demand.id} $statusColor={accentColor} onClick={(e) => {
                  if ((e.target as HTMLElement).closest('[data-dropdown]')) return;
                  handleRowClick(demand);
                }}>
                  <TableCell>
                    <TitleCellContent>
                      <TitleMain>{demand.titulo}</TitleMain>
                      <TitleMeta>
                        <TitleTag>{typeLabels[demand.tipo]}</TitleTag>
                        {demand.solicitante && (
                          <>
                            <TitleMetaDot />
                            <TitleMetaText>{demand.solicitante.nome}</TitleMetaText>
                          </>
                        )}
                        <TitleMetaDot />
                        <StatusInlineDot $color={accentColor} />
                        <TitleMetaText>{statusLabels[demand.status]}</TitleMetaText>
                      </TitleMeta>
                    </TitleCellContent>
                  </TableCell>

                  <TableCell>
                    <CityTableCell>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 13, height: 13, flexShrink: 0, color: '#94a3b8' }}>
                        <path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                      {demand.cidade?.nome || '—'}
                    </CityTableCell>
                  </TableCell>

                  <TableCell $allowOverflow>
                    <ResponsibleCell
                      $isAdmin={canAssignResponsible || canUnassignResponsible}
                      data-dropdown
                      onClick={(e) => (canAssignResponsible || canUnassignResponsible) ? handleResponsibleClick(e, demand.id) : undefined}
                    >
                      {assigningResponsible === demand.id ? (
                        <LoadingSpinner>Atribuindo...</LoadingSpinner>
                      ) : demand.responsavel ? (
                        <>
                          <ResponsibleAvatar>
                            <UserAvatar name={demand.responsavel.nome} fotoUrl={demand.responsavel.foto_url} size={28} />
                          </ResponsibleAvatar>
                          <ResponsibleName>{demand.responsavel.nome}</ResponsibleName>
                        </>
                      ) : (
                        <UnassignedText>
                          {canAssignResponsible ? 'Clique para atribuir' : 'Sem responsável'}
                        </UnassignedText>
                      )}
                      {(canAssignResponsible || canUnassignResponsible) && openResponsibleDropdown === demand.id && !assigningResponsible && (
                        <ResponsibleDropdown onClick={(e) => e.stopPropagation()}>
                          {demand.responsavel && canUnassignResponsible && (
                            <ResponsibleOption key="unassign" onClick={() => handleAssignResponsible(demand.id, null as any)} style={{ borderBottom: canAssignResponsible ? '1px solid #f1f5f9' : undefined }}>
                              <OptionAvatar style={{ background: '#f1f5f9' }}>
                                <svg fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                                  <path d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
                                </svg>
                              </OptionAvatar>
                              <OptionInfo>
                                <OptionName style={{ color: '#64748b', fontWeight: 500 }}>Remover atribuição</OptionName>
                              </OptionInfo>
                            </ResponsibleOption>
                          )}
                          {canAssignResponsible && teamMembers.map((member) => (
                            <ResponsibleOption key={member.id} onClick={() => handleAssignResponsible(demand.id, member.id)}>
                              <OptionAvatar><UserAvatar name={member.nome} fotoUrl={member.foto_url} size={36} /></OptionAvatar>
                              <OptionInfo>
                                <OptionName>{member.nome}</OptionName>
                                <OptionRole>{member.tipo_usuario === 'ADMIN' ? 'Administrador' : `Assessor · ${member.nivel_acesso}`}</OptionRole>
                              </OptionInfo>
                            </ResponsibleOption>
                          ))}
                        </ResponsibleDropdown>
                      )}
                    </ResponsibleCell>
                  </TableCell>

                  <TableCell>
                    <CadastradoCell>
                      <CadastradoDate>{formatDate(demand.data_cadastro)}</CadastradoDate>
                      <CadastradoRelative>{formatRelativeDate(demand.data_cadastro)}</CadastradoRelative>
                    </CadastradoCell>
                  </TableCell>

                  <TableCell>
                    {sla > 0 ? (
                      <SlaTableCell $color={slaColor}>
                        <SlaDash $color={slaColor} />
                        <SlaValue>{sla} {sla === 1 ? 'dia' : 'dias'}</SlaValue>
                      </SlaTableCell>
                    ) : (
                      <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
          {/* Desktop infinite scroll sentinel + shimmer */}
          {!loading && visibleCount < filteredDemands.length && (
            <div ref={sentinelRef} style={{ height: 1 }} />
          )}
          {!loading && loadingMore && (
            <>
              {[...Array(3)].map((_, i) => (
                <TableRow key={`shimmer-more-${i}`} $statusColor="#e2e8f0">
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '14px 0' }}>
                      <ShimmerLine width="60%" height="14px" />
                      <ShimmerLine width="40%" height="11px" />
                    </div>
                  </TableCell>
                  <TableCell><ShimmerLine width="80px" height="13px" /></TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ShimmerCircle style={{ width: 28, height: 28, minWidth: 28 }} />
                      <ShimmerLine width="90px" height="13px" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <ShimmerLine width="80px" height="13px" />
                      <ShimmerLine width="55px" height="11px" />
                    </div>
                  </TableCell>
                  <TableCell><ShimmerLine width="50px" height="13px" /></TableCell>
                </TableRow>
              ))}
            </>
          )}
        </DemandsTable>
        {!loading && visibleCount >= filteredDemands.length && filteredDemands.length > 40 && (
          <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 13, color: '#cbd5e1' }}>
            {filteredDemands.length} demandas exibidas
          </div>
        )}

        {/* Mobile list */}
        <MobileDemandList>
          {loading ? (
            [...Array(6)].map((_, i) => (
              <MobileDemandShimmer key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <ShimmerLine width="180px" height="16px" />
                  <ShimmerBadge width="80px" />
                </div>
                <ShimmerLine width="110px" height="13px" style={{ marginBottom: 10 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ShimmerCircle style={{ width: 24, height: 24, minWidth: 24 }} />
                  <ShimmerLine width="100px" height="13px" />
                </div>
              </MobileDemandShimmer>
            ))
          ) : filteredDemands.length === 0 ? (
            <EmptyState>
              <EmptyStateText>Nenhuma demanda encontrada</EmptyStateText>
              <EmptyStateSubtext>
                {demands.length === 0 ? 'Comece criando sua primeira demanda' : 'Tente ajustar os filtros de busca'}
              </EmptyStateSubtext>
            </EmptyState>
          ) : (
            groupedDemands.map(group => (
              <React.Fragment key={group.key}>
                <DemandsSectionHeader>
                  <DemandsSectionDot $color={group.dot} />
                  <DemandsSectionLabel $color={group.color}>{group.label}</DemandsSectionLabel>
                  <DemandsSectionCount>· {group.items.length}</DemandsSectionCount>
                </DemandsSectionHeader>
                {group.items.map(demand => {
                  const sla = calculateSLA(demand.data_cadastro, demand.status);
                  const slaColor = sla > 30 ? '#ef4444' : sla > 10 ? '#b45309' : '#10b981';
                  const borderColor = statusAccentColor[demand.status];
                  return (
                    <MobileNewDemandCard key={demand.id} $borderColor={borderColor} onClick={() => handleRowClick(demand)}>
                      <MobileNewDemandCardInner>
                        <MobileNewDemandTitleRow>
                          <MobileNewDemandTitle>{demand.titulo}</MobileNewDemandTitle>
                          <MobileNewDemandMenuBtn onClick={e => { e.stopPropagation(); handleRowClick(demand); }}>···</MobileNewDemandMenuBtn>
                        </MobileNewDemandTitleRow>
                        <MobileNewDemandMetaRow>
                          <MobileNewDemandTag>{typeLabels[demand.tipo]}</MobileNewDemandTag>
                          {demand.solicitante && (
                            <><MobileNewDemandMetaDot>·</MobileNewDemandMetaDot><MobileNewDemandMetaText>{demand.solicitante.nome}</MobileNewDemandMetaText></>
                          )}
                          {demand.cidade && (
                            <><MobileNewDemandMetaDot>·</MobileNewDemandMetaDot>
                            <MobileNewDemandMetaText>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: 2 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                              {demand.cidade.nome}
                            </MobileNewDemandMetaText></>
                          )}
                        </MobileNewDemandMetaRow>
                        <MobileNewDemandResponsibleRow>
                          <MobileNewDemandResponsibleLeft>
                            <UserAvatar name={demand.responsavel?.nome ?? ''} fotoUrl={demand.responsavel?.foto_url} size={26} />
                            {demand.responsavel
                              ? <MobileNewDemandResponsibleName>{demand.responsavel.nome}</MobileNewDemandResponsibleName>
                              : <span style={{ fontSize: 12, color: '#cbd5e1', fontStyle: 'italic' }}>Sem responsável</span>
                            }
                          </MobileNewDemandResponsibleLeft>
                          {sla > 0 && demand.status !== 'CONCLUIDA' && demand.status !== 'CANCELADA' && (
                            <MobileNewDemandSlaRight>
                              <MobileNewDemandSlaDash $color={slaColor} />
                              <MobileNewDemandSlaText $color={slaColor}>{sla} {sla === 1 ? 'dia' : 'dias'}</MobileNewDemandSlaText>
                            </MobileNewDemandSlaRight>
                          )}
                        </MobileNewDemandResponsibleRow>
                      </MobileNewDemandCardInner>
                      <MobileNewDemandFooter>
                        <MobileNewDemandStatusBadge $status={demand.status}>{statusLabels[demand.status]}</MobileNewDemandStatusBadge>
                        <MobileNewDemandDateText>cadastrado {formatDate(demand.data_cadastro)} · {formatRelativeDate(demand.data_cadastro)}</MobileNewDemandDateText>
                      </MobileNewDemandFooter>
                    </MobileNewDemandCard>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </MobileDemandList>

        {/* Mobile infinite scroll sentinel */}
        {!loading && visibleCount < filteredDemands.length && (
          <div ref={mobileSentinelRef} style={{ height: 1 }} />
        )}
        {!loading && loadingMore && (
          <>
            {[...Array(3)].map((_, i) => (
              <MobileDemandShimmer key={`shimmer-more-m-${i}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <ShimmerLine width="180px" height="16px" />
                  <ShimmerBadge width="80px" />
                </div>
                <ShimmerLine width="110px" height="13px" style={{ marginBottom: 10 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ShimmerCircle style={{ width: 24, height: 24, minWidth: 24 }} />
                  <ShimmerLine width="100px" height="13px" />
                </div>
              </MobileDemandShimmer>
            ))}
          </>
        )}
        {!loading && visibleCount >= filteredDemands.length && filteredDemands.length > 40 && (
          <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 13, color: '#cbd5e1' }}>
            {filteredDemands.length} demandas exibidas
          </div>
        )}

        {/* Mobile filter bottom sheet */}
        <DemandFilterOverlay $open={showMobileFilterSheet} onClick={() => setShowMobileFilterSheet(false)} />
        <DemandFilterSheet $open={showMobileFilterSheet}>
          <DemandFilterHandle />
          <DemandFilterSheetTitle>Filtros</DemandFilterSheetTitle>
          <DemandFilterSheetBody>
            <DemandFilterSection>
              <DemandFilterSectionTitle>Responsável</DemandFilterSectionTitle>
              <DemandFilterOptions>
                <DemandFilterOption $active={responsibleFilter === 'all'} onClick={() => setResponsibleFilter('all')}>Todos</DemandFilterOption>
                <DemandFilterOption $active={responsibleFilter === 'unassigned'} onClick={() => setResponsibleFilter('unassigned')}>Não atribuído</DemandFilterOption>
                {teamMembers.map(m => (
                  <DemandFilterOption key={m.id} $active={responsibleFilter === m.id.toString()} onClick={() => setResponsibleFilter(m.id.toString())}>{m.nome}</DemandFilterOption>
                ))}
              </DemandFilterOptions>
            </DemandFilterSection>
            <DemandFilterSection>
              <DemandFilterSectionTitle>Status</DemandFilterSectionTitle>
              <DemandFilterOptions>
                {(['all', 'NAO_INICIADA', 'EM_ANDAMENTO', 'PAUSADA', 'CONCLUIDA', 'CANCELADA'] as const).map(s => (
                  <DemandFilterOption key={s} $active={statusFilter === s} onClick={() => setStatusFilter(s)}>
                    {s === 'all' ? 'Todos' : statusLabels[s]}
                  </DemandFilterOption>
                ))}
              </DemandFilterOptions>
            </DemandFilterSection>
            <DemandFilterSection>
              <DemandFilterSectionTitle>Ordenar</DemandFilterSectionTitle>
              <DemandFilterOptions>
                <DemandFilterOption $active={!sortColumn} onClick={() => setSortColumn('')}>Padrão</DemandFilterOption>
                <DemandFilterOption $active={sortColumn === 'sla' && sortDirection === 'desc'} onClick={() => { setSortColumn('sla'); setSortDirection('desc'); }}>Maior SLA</DemandFilterOption>
                <DemandFilterOption $active={sortColumn === 'sla' && sortDirection === 'asc'} onClick={() => { setSortColumn('sla'); setSortDirection('asc'); }}>Menor SLA</DemandFilterOption>
                <DemandFilterOption $active={sortColumn === 'data' && sortDirection === 'desc'} onClick={() => { setSortColumn('data'); setSortDirection('desc'); }}>Mais recentes</DemandFilterOption>
                <DemandFilterOption $active={sortColumn === 'data' && sortDirection === 'asc'} onClick={() => { setSortColumn('data'); setSortDirection('asc'); }}>Mais antigas</DemandFilterOption>
                <DemandFilterOption $active={sortColumn === 'titulo'} onClick={() => { setSortColumn('titulo'); setSortDirection('asc'); }}>Título A–Z</DemandFilterOption>
              </DemandFilterOptions>
            </DemandFilterSection>
          </DemandFilterSheetBody>
          <DemandFilterApplyBtn onClick={() => setShowMobileFilterSheet(false)}>Aplicar filtros</DemandFilterApplyBtn>
        </DemandFilterSheet>

        </DemandsContainer>
      )}

      <NewDemandModal
        isOpen={isSideSheetOpen}
        onClose={() => setIsSideSheetOpen(false)}
        onSubmit={handleDemandSubmit}
      />

      <DemandModalController
        controllerRef={modalControllerRef}
        onDemandUpdate={handleDemandUpdate}
        canViewUpdates={canViewUpdates}
        canCreateUpdates={canCreateUpdates}
        canEditUpdate={canEditUpdate}
        canDeleteUpdate={canDeleteUpdate}
        canEditDemand={canEditDemand}
        canDeleteDemand={canDeleteDemand}
        canAssignResponsible={canAssignResponsible}
        canUnassignResponsible={canUnassignResponsible}
        userData={userData}
        session={session}
      />
    </>
  );
};

export default Demands;

const AccessDeniedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 60px 20px;
`;

const AccessDeniedIcon = styled.div`
  color: #94a3b8;
  margin-bottom: 24px;
`;

const AccessDeniedTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
`;

const AccessDeniedText = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0;
  text-align: center;
  max-width: 400px;
`;
